import React from 'react';
import { Activity, UserPlus, LogIn, Megaphone, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '../common/DashboardComponents';

/**
 * Recent Activities Component
 * Displays recent system activities in a timeline
 */
export function RecentActivities({ activities, limit = 10 }) {
  if (!activities || activities.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h3>
          <EmptyState
            icon={Activity}
            title="No recent activities"
            description="System activities will appear here"
          />
        </CardContent>
      </Card>
    );
  }

  const displayActivities = activities.slice(0, limit);

  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case 'USER_CREATED':
        return UserPlus;
      case 'USER_LOGIN':
        return LogIn;
      case 'CAMPAIGN_CREATED':
        return Megaphone;
      default:
        return Activity;
    }
  };

  const getActivityColor = (activityType) => {
    switch (activityType) {
      case 'USER_CREATED':
        return 'bg-green-100 text-green-600';
      case 'USER_LOGIN':
        return 'bg-blue-100 text-blue-600';
      case 'CAMPAIGN_CREATED':
        return 'bg-purple-100 text-purple-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      
      return date.toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activities</h3>
          <Clock className="w-5 h-5 text-gray-400" />
        </div>
        
        <div className="space-y-4">
          {displayActivities.map((activity, index) => {
            const Icon = getActivityIcon(activity.activity_type);
            const colorClass = getActivityColor(activity.activity_type);
            
            return (
              <div key={index} className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${colorClass}`}>
                  <Icon className="w-5 h-5" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {activity.description}
                  </p>
                  {activity.user_email && (
                    <p className="text-xs text-gray-500 mt-1">
                      {activity.user_email}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {formatTimestamp(activity.timestamp)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {activities.length > limit && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              View all activities →
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default RecentActivities;
